
import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GeminiService } from '../../../services/gemini.service';
import { NotificationService } from '../../../services/notification.service';

@Component({
  selector: 'app-platform-marketing',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [GeminiService],
  templateUrl: './platform-marketing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlatformMarketingComponent {
  private geminiService = inject(GeminiService);
  private notificationService = inject(NotificationService);

  marketingTopic = signal('');
  marketingFormat = signal('Facebook Post');
  marketingTone = signal('ทางการแพทย์และน่าเชื่อถือ (Medical Professional)');
  generatedContent = signal('');

  marketingImagePrompt = signal('');
  generatedImageUrl = signal('');

  strategyGoal = signal('');
  strategyAudience = signal('');
  generatedStrategy = signal('');

  videoTopic = signal('');
  videoDuration = signal('Short (15-30s)');
  generatedScript = signal('');

  isGeneratingMarketing = signal(false);

  featureList = [
      { key: 'core_pos', label: 'ระบบจุดขาย (Core POS)' },
      { key: 'telemedicine', label: 'แพทย์ทางไกล (Telemedicine)' },
      { key: 'reporting', label: 'รายงานผล (Reporting)' },
      { key: 'ai_order', label: 'สั่งงานด้วย AI (AI Order)' },
      { key: 'loyalty', label: 'ระบบสมาชิก (Loyalty)' },
      { key: 'partner_network', label: 'เครือข่ายพันธมิตร (Partner Network)' },
      { key: 'accounting', label: 'บัญชี (Accounting)' },
      { key: 'compliance_reports', label: 'รายงาน ภท. (Compliance)' },
      { key: 'multi_store', label: 'จัดการหลายสาขา (Multi-Store)' },
      { key: 'kiosk', label: 'ตู้ลงทะเบียนคนไข้ (Patient Kiosk)' } 
  ];

  get featureLabels(): string[] { return this.featureList.map(f => f.label); }

  async generateContent() {
      if (!this.marketingTopic().trim()) return;
      this.isGeneratingMarketing.set(true);
      try {
          const content = await this.geminiService.generateMarketingContent(this.marketingTopic(), this.marketingFormat(), this.marketingTone(), this.featureLabels);
          this.generatedContent.set(content);
      } catch (e: any) { 
          this.notificationService.showError("Content Gen Failed: " + e.message); 
      } finally { 
          this.isGeneratingMarketing.set(false); 
      }
  }

  async generateImage() {
      if (!this.marketingImagePrompt().trim()) return;
      this.isGeneratingMarketing.set(true);
      try {
          const url = await this.geminiService.generateMarketingImage(this.marketingImagePrompt());
          this.generatedImageUrl.set(url);
      } catch (e: any) { 
          this.notificationService.showError("Image Gen Failed: " + e.message); 
      } finally { 
          this.isGeneratingMarketing.set(false); 
      }
  }

  async generateStrategy() {
      if (!this.strategyGoal().trim() || !this.strategyAudience().trim()) return;
      this.isGeneratingMarketing.set(true);
      try {
          const content = await this.geminiService.generateMarketingStrategy(this.strategyGoal(), this.strategyAudience(), this.featureLabels);
          this.generatedStrategy.set(content);
      } catch (e: any) { 
          this.notificationService.showError("Strategy Gen Failed: " + e.message); 
      } finally { 
          this.isGeneratingMarketing.set(false); 
      }
  }

  async generateVideoScript() {
      if (!this.videoTopic().trim()) return;
      this.isGeneratingMarketing.set(true);
      try {
          const content = await this.geminiService.generateVideoScript(this.videoTopic(), this.videoDuration(), this.featureLabels);
          this.generatedScript.set(content);
      } catch (e: any) { 
          this.notificationService.showError("Video Script Gen Failed: " + e.message); 
      } finally { 
          this.isGeneratingMarketing.set(false); 
      }
  }

  copyContent(text: string) { 
      navigator.clipboard.writeText(text); 
      this.notificationService.showSuccess("คัดลอกเรียบร้อย! (Copied)"); 
  }
}
